/* eslint-disable react/prop-types */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ANSWERS, SIGN_IN_KINDS, flagsFor, buildLabel, paramValue } from './installsModel';
import { fmtDelta, isoUtc, timeCell } from './installsFormat';

// Every value below came off a phone: it is rendered as React text, never as HTML.

const userHref = (id) => `/user-detail/${btoa(String(id))}`;

// Shown first in the parameter grid; the rest sit behind "Show N more".
const PARAM_PRIORITY = [
  'event_name', 'app_name', 'build_number', 'device_id', 'device_model', 'install_source', 'locale_name',
  'os', 'pkg_name', 'previous_first_open_count', 'store', 'version', 'os_install_time', 'os_last_update_time',
  'system_app', 'system_app_update', 'update_with_analytics', 'timezone_id',
];

const answerTitle = (key, row) => {
  if (key === 'signedIn') return SIGN_IN_KINDS[row.signInKind]?.title ?? 'Signed in';
  return ANSWERS.find((a) => a.key === key).event;
};
const anchorWord = (row) => (row.anchor === 'install_event' ? 'the install event' : 'the first open');

export function AnswerCell({ row, answer }) {
  const t = row.followUps?.[answer.key];
  if (t !== null && t !== undefined) {
    const delta = fmtDelta(t - row.timestampMillis);
    const kind = answer.key === 'signedIn' ? SIGN_IN_KINDS[row.signInKind] : null;
    return (
      <span className="inst-pill inst-pill--yes" title={`${answerTitle(answer.key, row)} — ${isoUtc(t)} UTC, ${delta} after ${anchorWord(row)}`}>
        ✓ {delta}
        {kind && <span className={`inst-tag inst-tag--${row.signInKind}`}>{kind.tag}</span>}
      </span>
    );
  }
  if (row.pending) {
    return <span className="inst-pill inst-pill--pending" title={`Window closes ${isoUtc(row.windowEndMillis)} UTC`}>pending</span>;
  }
  const what = answer.key === 'signedIn' ? 'sign-in' : answer.key === 'signInStart' ? 'sign-in start' : answer.event;
  return <span className="inst-no" title={`No ${what} within 48 hours of ${anchorWord(row)}`}>✗</span>;
}

function CopyButton({ value, onCopy }) {
  return <button type="button" className="inst-copy" onClick={() => onCopy(value)}>Copy</button>;
}

function Detail({ row, onCopy }) {
  const [allParams, setAllParams] = useState(false);
  const fu = row.followUps || {};
  const at = (t) => `${isoUtc(t)} UTC · ${fmtDelta(t - row.timestampMillis)}`;
  const missing = row.pending ? 'not yet' : 'none within 48 hours';
  const signedLabel = SIGN_IN_KINDS[row.signInKind]?.event ?? 'signed in';
  const lines = [
    { name: row.eventName, when: `${isoUtc(row.timestampMillis)} UTC` },
    { name: 'session_start', t: fu.opened },
    { name: 'pauser_app_install', t: fu.install },
    { name: 'sign-in started', t: fu.signInStart },
    { name: signedLabel, t: fu.signedIn },
  ].map((l) => (l.when ? l : { ...l, when: l.t != null ? at(l.t) : missing, none: l.t == null }));
  if (row.signInErrorMillis != null) lines.push({ name: 'sign-in error', when: at(row.signInErrorMillis) });
  if (row.signInCancelMillis != null) lines.push({ name: 'sign-in cancelled', when: at(row.signInCancelMillis) });
  lines.push({ name: row.pending ? 'window closes' : 'window closed', when: `${isoUtc(row.windowEndMillis)} UTC` });

  const anchorDevice = paramValue(row, 'device_id');
  const deviceId = anchorDevice ?? row.installDeviceId;
  let deviceNote = 'Neither the first open nor an install event carried one';
  if (anchorDevice) deviceNote = row.anchor === 'install_event' ? 'From the install event' : 'From the first open';
  else if (row.installDeviceId) deviceNote = 'From the install event — the first open carried none';

  const params = [{ key: 'event_name', value: row.eventName }, ...(row.params || [])];
  const rank = (k) => { const i = PARAM_PRIORITY.indexOf(k); return i === -1 ? PARAM_PRIORITY.length : i; };
  const ordered = [...params].sort((a, b) => rank(a.key) - rank(b.key));
  const lead = ordered.filter((p) => rank(p.key) < PARAM_PRIORITY.length);
  const shown = allParams ? ordered : lead;
  const hidden = ordered.length - lead.length;

  return (
    <>
      <div className="inst-detail-grid">
        <section>
          <h4 className="inst-h">What happened</h4>
          <ol className="inst-events">
            {lines.map((l) => (
              <li key={l.name} className={l.none ? 'inst-ev--none' : ''}><span>{l.name}</span><span className="inst-ev-when">{l.when}</span></li>
            ))}
          </ol>
        </section>
        <section>
          <h4 className="inst-h">Identities</h4>
          <dl className="inst-ids">
            <dt>user_pseudo_id</dt>
            <dd>
              <span className="inst-idval">{row.userPseudoId}</span><CopyButton value={row.userPseudoId} onCopy={onCopy} />
              <span className="inst-idnote">This installation — what every answer is matched on</span>
            </dd>
            <dt>device_id</dt>
            <dd>
              {deviceId ? <><span className="inst-idval">{deviceId}</span><CopyButton value={deviceId} onCopy={onCopy} /></> : <span>none</span>}
              <span className="inst-idnote">{deviceNote}</span>
            </dd>
            {row.signedInUserId && (
              <>
                <dt>user_id</dt>
                <dd>
                  <span className="inst-idval">{row.signedInUserId}</span><CopyButton value={row.signedInUserId} onCopy={onCopy} />
                  <span className="inst-idnote">The account that signed in — the backend users.id. <Link to={userHref(row.signedInUserId)}>User 360 →</Link></span>
                </dd>
              </>
            )}
          </dl>
        </section>
      </div>
      <section className="inst-params">
        <h4 className="inst-h">{row.eventName} parameters</h4>
        <div className="inst-pgrid">
          {shown.map((p) => (
            <div key={p.key}><span className="inst-pkey">{p.key}</span><span className="inst-pval">{p.value ?? '—'}</span></div>
          ))}
        </div>
        {hidden > 0 && (
          <button type="button" className="inst-btn inst-more" onClick={() => setAllParams((v) => !v)}>
            {allParams ? 'Show fewer' : `Show ${hidden} more`}
          </button>
        )}
      </section>
    </>
  );
}

/** Clicks on links, flag chips, buttons, or while text is selected do not toggle the row. */
const shouldToggle = (e) => {
  if (e.target.closest && e.target.closest('a, button, input, .inst-detail')) return false;
  const sel = typeof window !== 'undefined' && window.getSelection ? String(window.getSelection()) : '';
  return !sel;
};

export default function InstallsTable({
  rows, firstHeader, sort, onSort, expanded, onToggle, onFlag, onCopy, showBuild, zone, testId,
}) {
  const header = (label, field) => {
    if (!field) return <th key={label}>{label}</th>;
    const active = sort.field === field;
    const arrow = active ? (sort.dir === 'desc' ? ' ↓' : ' ↑') : ' ↕';
    return (
      <th key={label} aria-sort={active ? (sort.dir === 'desc' ? 'descending' : 'ascending') : 'none'}>
        <button type="button" data-active={active} onClick={() => onSort(field)}>{label}{arrow}</button>
      </th>
    );
  };
  return (
    <div className="inst-tablewrap">
      <table className="inst-table" data-testid={testId}>
        <thead>
          <tr>
            {header(firstHeader, 'first-open')}
            {header('User')}
            {header('Device')}
            {header('Version')}
            {header('Opened app', 'opened')}
            {header('Install event', 'install')}
            {header('Sign-in started', 'signInStart')}
            {header('Signed in', 'signedIn')}
            {header('Flags')}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const open = expanded.has(row.userPseudoId);
            const time = timeCell(row.timestampMillis, zone);
            const flags = flagsFor(row);
            return (
              <React.Fragment key={`${row.anchor}:${row.userPseudoId}`}>
                <tr className={`inst-row${open ? ' inst-row--open' : ''}`} onClick={(e) => { if (shouldToggle(e)) onToggle(row.userPseudoId); }} aria-expanded={open}>
                  <td className="inst-time">
                    <span className="inst-chev">{open ? '▾' : '▸'}</span>{time.utc}
                    {time.local && <span className="inst-sub">{time.local}</span>}
                  </td>
                  <td>
                    {row.signedInUserId
                      ? <Link className="inst-userlink" to={userHref(row.signedInUserId)}>{row.signedInUserId}</Link>
                      : <span className="inst-dash" title={row.pending ? 'Not signed in yet' : 'Not signed in within 48 hours'}>—</span>}
                  </td>
                  <td>
                    {row.deviceModel || 'Unknown device'}
                    <span className="inst-sub">{[row.osVersion, row.country].filter(Boolean).join(' · ')}</span>
                  </td>
                  <td>
                    {row.appVersion ? `v${row.appVersion}` : <span className="inst-dash">—</span>}
                    {showBuild && row.target && <span className="inst-sub">{buildLabel(row.target)}</span>}
                  </td>
                  {ANSWERS.map((a) => <td key={a.key}><AnswerCell row={row} answer={a} /></td>)}
                  <td>
                    {flags.length ? (
                      <span className="inst-flags">
                        {flags.map((f) => (
                          <button type="button" key={f.key} className={`inst-flag inst-flag--${f.tone}`} onClick={() => onFlag(f.key, row)} title={f.label}>
                            {f.chip(row)}
                          </button>
                        ))}
                      </span>
                    ) : <span className="inst-dash">—</span>}
                  </td>
                </tr>
                {open && (
                  <tr className="inst-detail"><td colSpan={9}><Detail row={row} onCopy={onCopy} /></td></tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
